/**
 * StatusTransitionDialog - Unified dialog for status transitions
 * Shows appropriate UI based on transition type (confirm, notes, cancel, complete, bay_select, quote)
 */

import { useState, useCallback, useEffect, type ChangeEvent } from 'react';
import { AlertTriangle, ArrowRight, Loader2, MessageSquare, CheckCircle, Wrench, Plus, Trash2, DollarSign, CheckSquare, UploadCloud, Image as ImageIcon, Send } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Button,
  Label,
  Input,
  Textarea,
  Checkbox,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '../ui';
import { cn } from '../../lib/utils';
import { getApiBase } from '../../lib/apiBase';
import { api } from '../../lib/api';
import type { TransitionDialogState } from '../../hooks/useStatusTransition';
import { CANCELLATION_REASONS, ESTIMATE_LINE_ITEM_CATEGORIES, BAY_TYPE_LABELS, type BayType } from '../../services/statusTransitionService';
import { useBaysByType } from '../../hooks/useBays';

/** Line item for estimate/quote */
export interface EstimateLineItem {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number; // In cents
  category: string;
}

interface StatusTransitionDialogProps {
  /** Dialog state from useStatusTransition */
  state: TransitionDialogState | null;
  /** Whether transition is in progress */
  isTransitioning: boolean;
  /** Error message if any */
  error: Error | null;
  /** Execute the transition */
  onExecute: (params: {
    notes?: string;
    publicNote?: string;
    reason?: string;
    bayId?: string;
    lineItems?: EstimateLineItem[];
    sendEmail?: boolean;
    checklistValues?: Array<{ itemKey: string; value: boolean | number | string | Record<string, unknown> | null }>;
    customFields?: Record<string, unknown>;
  }) => Promise<void>;
  /** Cancel and close dialog */
  onCancel: () => void;
  /** Service bay type for filtering compatible bays */
  serviceBayType?: BayType;
  /** Optional status metadata override (e.g., job workflow) */
  statusMetadata?: Record<string, { label: string; textColor: string; bgColor: string; borderColor?: string }>;
  /** Optional callback after successful attachment upload */
  onUploadComplete?: (bookingId: string) => void;
}

/** Generate unique ID for line items */
function generateId(): string {
  return Math.random().toString(36).substring(2, 11);
}

/** Create empty line item */
function createEmptyLineItem(): EstimateLineItem {
  return {
    id: generateId(),
    description: '',
    quantity: 1,
    unitPrice: 0,
    category: 'labor',
  };
}

export function StatusTransitionDialog({
  state,
  isTransitioning,
  error: externalError,
  onExecute,
  onCancel,
  serviceBayType,
  statusMetadata,
  onUploadComplete,
}: StatusTransitionDialogProps) {
  const apiBase = getApiBase();
  const [notes, setNotes] = useState('');
  const [publicNote, setPublicNote] = useState('');
  const [reason, setReason] = useState('');
  const [selectedBayId, setSelectedBayId] = useState('');
  const [localError, setLocalError] = useState<string | null>(null);
  const [lineItems, setLineItems] = useState<EstimateLineItem[]>([]);
  const [checklistValues, setChecklistValues] = useState<Record<string, boolean | number | string | Record<string, unknown> | null>>({});
  const [customFields, setCustomFields] = useState<Record<string, unknown>>({});
  const [requirements, setRequirements] = useState(state?.config.requirements || []);
  const [uploadingKeys, setUploadingKeys] = useState<Set<string>>(new Set());
  const [uploadingFileCounts, setUploadingFileCounts] = useState<Record<string, number>>({});
  const [uploadSuccessByKey, setUploadSuccessByKey] = useState<Record<string, number>>({});
  const [uploadPreviewsByKey, setUploadPreviewsByKey] = useState<Record<string, string[]>>({});
  const [sendingSignatureKey, setSendingSignatureKey] = useState<string | null>(null);
  const [signatureSentByKey, setSignatureSentByKey] = useState<Record<string, boolean>>({});

  // Fetch bays of the required type when bay selection is needed
  const requiresBaySelection = state?.config.dialogType === 'bay_select';
  const requiresQuote = state?.config.dialogType === 'quote';

  const { data: availableBays, isLoading: isLoadingBays } = useBaysByType(
    requiresBaySelection ? serviceBayType : undefined
  );

  // Reset form when dialog opens/closes
  useEffect(() => {
    if (state?.isOpen) {
      setNotes('');
      setPublicNote('');
      setReason('');
      setSelectedBayId('');
      setLocalError(null);
      setChecklistValues({});
      setCustomFields(state.customFieldValues || {});
      setRequirements(state.config.requirements || []);
      setUploadingKeys(new Set());
      setUploadingFileCounts({});
      setUploadSuccessByKey({});
      setUploadPreviewsByKey({});
      setSendingSignatureKey(null);
      setSignatureSentByKey({});
      // Initialize with one empty line item for quote dialog
      if (state.config.dialogType === 'quote') {
        setLineItems([createEmptyLineItem()]);
      } else {
        setLineItems([]);
      }
    }
  }, [state?.isOpen, state?.config.dialogType]);

  // Line item management functions
  const addLineItem = useCallback(() => {
    setLineItems(prev => [...prev, createEmptyLineItem()]);
  }, []);

  const removeLineItem = useCallback((id: string) => {
    setLineItems(prev => prev.filter(item => item.id !== id));
  }, []);

  const updateLineItem = useCallback((id: string, field: keyof EstimateLineItem, value: string | number) => {
    setLineItems(prev => prev.map(item => {
      if (item.id !== id) return item;
      return { ...item, [field]: value };
    }));
  }, []);

  // Calculate total estimate
  const totalEstimate = lineItems.reduce((sum, item) => {
    return sum + (item.quantity * item.unitPrice);
  }, 0);

  const dialogConfig = (state?.config.dialogConfig || {}) as {
    title?: string;
    description?: string;
    showNotes?: boolean;
    notesRequired?: boolean;
    showPublicNote?: boolean;
    customFields?: Array<{
      name: string;
      type: string;
      label: string;
      required: boolean;
      placeholder?: string;
      options?: Array<{ value: string; label: string }>;
      accept?: string;
      multiple?: boolean;
      min?: number;
      max?: number;
      pattern?: string;
    }>;
  };
  const customFieldDefs = dialogConfig.customFields || [];
  const notifyUploadComplete = useCallback(() => {
    if (!state?.bookingId || !onUploadComplete) return;
    onUploadComplete(state.bookingId);
  }, [onUploadComplete, state?.bookingId]);

  const handleExecute = useCallback(async () => {
    if (!state) return;

    // Validate cancellation reason
    if (state.config.dialogType === 'cancel' && !reason) {
      setLocalError('Por favor, selecciona un motivo de cancelación');
      return;
    }

    if (dialogConfig.notesRequired && !isCancellation && !notes.trim()) {
      setLocalError('Por favor, añade una nota interna');
      return;
    }

    // Validate bay selection
    if (requiresBaySelection && !selectedBayId) {
      setLocalError('Por favor, selecciona un puesto de trabajo');
      return;
    }

    // Validate quote line items
    if (requiresQuote) {
      const validItems = lineItems.filter(item => item.description.trim() && item.unitPrice > 0);
      if (validItems.length === 0) {
        setLocalError('Por favor, añade al menos un concepto con descripción y precio');
        return;
      }
    }

    // Validate checklist requirements
    const missingRequired = requirements.filter((req) => {
      if (!req.config.isRequired) return false;
      if (req.config.itemType === 'photo' || req.config.itemType === 'signature' || req.config.itemType === 'file') {
        return !req.isComplete;
      }
      const value = checklistValues[req.config.itemKey];
      if (req.config.itemType === 'boolean') {
        return value !== true;
      }
      return value === null || value === undefined || value === '';
    });
    if (missingRequired.length > 0) {
      setLocalError('Por favor, completa todos los requisitos obligatorios');
      return;
    }

    const missingCustomFields = customFieldDefs.filter((field) => {
      if (!field.required) return false;
      const value = customFields[field.name];
      if (field.type === 'checkbox') return value !== true;
      return value === null || value === undefined || value === '';
    });
    if (missingCustomFields.length > 0) {
      setLocalError('Completa los campos requeridos antes de continuar');
      return;
    }

    setLocalError(null);

    try {
      // Filter out empty line items before sending
      const validLineItems = lineItems.filter(item => item.description.trim() && item.unitPrice > 0);

      await onExecute({
        notes: notes.trim() || undefined,
        publicNote: publicNote.trim() || undefined,
        reason: reason || undefined,
        bayId: selectedBayId || undefined,
        lineItems: requiresQuote ? validLineItems : undefined,
        checklistValues: Object.entries(checklistValues).map(([itemKey, value]) => ({
          itemKey,
          value,
        })),
        customFields,
      });
    } catch (err) {
      // Error handled by parent
    }
  }, [state, requiresBaySelection, requiresQuote, notes, publicNote, reason, selectedBayId, lineItems, checklistValues, customFields, customFieldDefs, onExecute]);

  if (!state) return null;

  const metadata = statusMetadata || {};
  const fromMeta = metadata[state.fromStatus];
  const toMeta = metadata[state.toStatus];
  const error = localError || externalError?.message;

  const isCancellation = state.config.dialogType === 'cancel';
  const isCompletion = state.config.dialogType === 'complete';
  const isBaySelect = state.config.dialogType === 'bay_select';
  const isQuote = state.config.dialogType === 'quote';
  const getEffectiveRequirementValue = (req: typeof requirements[number]) =>
    checklistValues[req.config.itemKey] ?? req.value;
  const isRequirementComplete = (req: typeof requirements[number]) => {
    if (!req.config.isRequired) return true;
    if (req.config.itemType === 'photo' || req.config.itemType === 'file') {
      const count = (req.value as { count?: number } | undefined)?.count ?? 0;
      const minCount = Number(req.config.validationRules?.minCount ?? 1);
      return count >= minCount;
    }
    if (req.config.itemType === 'signature') {
      return req.isComplete;
    }
    const value = getEffectiveRequirementValue(req);
    if (req.config.itemType === 'boolean') {
      return value === true;
    }
    return value !== null && value !== undefined && value !== '';
  };

  const requirementErrorSet = new Set<string>();
  requirements.forEach((req) => {
    // Collect ALL requirement-related error messages (complete or not)
    // so they are filtered from the top-level blocking banner.
    // Incomplete requirements are shown inline; we don't want them
    // duplicated as blocking errors that disable the button.
    if (req.config.isRequired) {
      requirementErrorSet.add(`${req.config.itemLabel} es obligatorio`);
    }
    if (req.errors && req.errors.length > 0) {
      req.errors.forEach((err) => requirementErrorSet.add(err));
    }
  });
  const nonRequirementErrors =
    state.config.errors?.filter((err) => !requirementErrorSet.has(err)) ?? [];
  const hasBlockingErrors =
    nonRequirementErrors.length > 0 ||
    (state.config.canExecute === false && requirements.length === 0);
  const hasIncompleteRequirements = requirements.some((req) => !isRequirementComplete(req));
  const hasIncompleteCustomFields = customFieldDefs.some((field) => {
    if (!field.required) return false;
    const value = customFields[field.name];
    if (field.type === 'checkbox') return value !== true;
    return value === null || value === undefined || value === '';
  });

  useEffect(() => {
    if (!localError) return;

    if (localError === 'Por favor, completa todos los requisitos obligatorios' && !hasIncompleteRequirements) {
      setLocalError(null);
      return;
    }

    if (localError === 'Completa los campos requeridos antes de continuar' && !hasIncompleteCustomFields) {
      setLocalError(null);
      return;
    }

    if (localError === 'Por favor, selecciona un motivo de cancelación' && reason) {
      setLocalError(null);
      return;
    }

    if (localError === 'Por favor, añade una nota interna' && notes.trim()) {
      setLocalError(null);
      return;
    }

    if (localError === 'Por favor, selecciona un puesto de trabajo' && selectedBayId) {
      setLocalError(null);
      return;
    }

    if (localError === 'Por favor, añade al menos un concepto con descripción y precio') {
      const validItems = lineItems.filter(item => item.description.trim() && item.unitPrice > 0);
      if (validItems.length > 0) setLocalError(null);
    }
  }, [
    localError,
    hasIncompleteRequirements,
    hasIncompleteCustomFields,
    reason,
    notes,
    selectedBayId,
    lineItems,
  ]);

  useEffect(() => {
    return () => {
      Object.values(uploadPreviewsByKey).flat().forEach((url) => URL.revokeObjectURL(url));
    };
  }, [uploadPreviewsByKey]);

  const completionChecks: Array<{ label: string; complete: boolean }> = [
    ...requirements
      .filter((req) => req.config.isRequired)
      .map((req) => ({ label: req.config.itemLabel, complete: isRequirementComplete(req) })),
    ...customFieldDefs
      .filter((field) => field.required)
      .map((field) => {
        const value = customFields[field.name];
        const complete = field.type === 'checkbox' ? value === true : value !== null && value !== undefined && value !== '';
        return { label: field.label, complete };
      }),
    ...(isCancellation ? [{ label: 'Motivo de cancelación', complete: Boolean(reason) }] : []),
    ...(isBaySelect ? [{ label: 'Puesto de trabajo', complete: Boolean(selectedBayId) }] : []),
    ...(dialogConfig.notesRequired && !isCancellation ? [{ label: 'Nota interna', complete: Boolean(notes.trim()) }] : []),
    ...(isQuote
      ? [{ label: 'Conceptos del presupuesto', complete: lineItems.filter((item) => item.description.trim() && item.unitPrice > 0).length > 0 }]
      : []),
  ];
  const completedChecks = completionChecks.filter((item) => item.complete).length;
  const missingChecks = completionChecks.filter((item) => !item.complete).map((item) => item.label);

  return (
    <Dialog open={state.isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className={cn('sm:max-w-md max-h-[90dvh] min-h-[60dvh] w-full overflow-hidden grid grid-rows-[auto,1fr,auto]', isQuote && 'sm:max-w-2xl')}>
        <DialogHeader>
          <DialogTitle className={cn(
            'flex items-center gap-2',
            isCancellation && 'text-red-600',
            isCompletion && 'text-green-600',
            isBaySelect && 'text-cyan-600',
            isQuote && 'text-yellow-600'
          )}>
            {isCancellation && <AlertTriangle className="h-5 w-5" />}
            {isCompletion && <CheckCircle className="h-5 w-5" />}
            {isBaySelect && <Wrench className="h-5 w-5" />}
            {isQuote && <DollarSign className="h-5 w-5" />}
            {!isCancellation && !isCompletion && !isBaySelect && !isQuote && <MessageSquare className="h-5 w-5" />}
            {dialogConfig.title || 'Cambiar Estado'}
          </DialogTitle>
          <DialogDescription>
            {dialogConfig.description}
          </DialogDescription>
        </DialogHeader>

        <div className="min-h-0 overflow-y-auto space-y-4 py-4">
          {/* Status transition visualization */}
          <div className="flex items-center justify-center gap-3 py-3 px-4 rounded-lg bg-gray-50">
            <span
              className="px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ color: fromMeta?.textColor, backgroundColor: fromMeta?.bgColor, borderColor: fromMeta?.borderColor }}
            >
              {fromMeta?.label || state.fromStatus}
            </span>
            <ArrowRight className="h-4 w-4 text-gray-400" />
            <span
              className="px-2.5 py-1 rounded-full text-xs font-medium"
              style={{ color: toMeta?.textColor, backgroundColor: toMeta?.bgColor, borderColor: toMeta?.borderColor }}
            >
              {toMeta?.label || state.toStatus}
            </span>
          </div>

          {completionChecks.length > 0 && (
            <div className="rounded-lg border border-blue-100 bg-blue-50/70 p-3 transition-all duration-200">
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-blue-900">Progreso del formulario</span>
                <span className="text-blue-700">{completedChecks}/{completionChecks.length} requisitos completados</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-blue-100">
                <div
                  className="h-full rounded-full bg-blue-500 transition-all duration-300"
                  style={{ width: `${completionChecks.length ? (completedChecks / completionChecks.length) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Booking info */}
          {(state.bookingReference || state.customerName) && (
            <div className="rounded-lg bg-gray-50 p-3 space-y-1.5">
              {state.bookingReference && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Referencia:</span>
                  <span className="font-medium font-mono">{state.bookingReference}</span>
                </div>
              )}
              {state.customerName && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Cliente:</span>
                  <span className="font-medium">{state.customerName}</span>
                </div>
              )}
            </div>
          )}

          {/* Transition constraints */}
          {nonRequirementErrors.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
              <p className="font-medium mb-1">Bloqueos de transición</p>
              <ul className="list-disc pl-4 space-y-1">
                {nonRequirementErrors.map((err) => (
                  <li key={err}>{err}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Status requirements */}
          {requirements.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <CheckSquare className="h-4 w-4" />
                Requisitos del estado
              </Label>
              <div className="space-y-3">
                {requirements.map((req) => {
                  const value = getEffectiveRequirementValue(req);
                  const isComplete = isRequirementComplete(req);
                  const isRequired = req.config.isRequired;
                  const isAttachment = ['photo', 'signature', 'file'].includes(req.config.itemType);
                  const descriptionText = req.config.validationRules?.description;
                  const hasDescription = descriptionText !== undefined && descriptionText !== null && String(descriptionText).trim() !== '';
                  const source = req.config.validationRules?.source;

                  return (
                    <div key={req.config.itemKey} className={cn('rounded-lg border p-3 transition-all duration-200', isComplete ? 'border-gray-200 bg-white' : 'border-amber-200 bg-amber-50/40')}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-gray-900">
                            {req.config.itemLabel}
                            {isRequired && <span className="text-red-500 ml-1">*</span>}
                          </p>
                          {hasDescription && (
                            <p className="text-xs text-gray-500 mt-0.5">
                              {String(descriptionText)}
                            </p>
                          )}
                        </div>
                        {!isAttachment && (
                          <span className={cn(
                            'text-[10px] px-2 py-0.5 rounded-full border',
                            isComplete ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'
                          )}>
                            {isComplete ? 'Completo' : 'Pendiente'}
                          </span>
                        )}
                      </div>

                      {req.config.itemType === 'boolean' && (
                        <div className="mt-2 flex items-center gap-2">
                          <Checkbox
                            checked={value === true}
                            onCheckedChange={(checked: boolean) =>
                              setChecklistValues((prev) => ({
                                ...prev,
                                [req.config.itemKey]: checked === true,
                              }))
                            }
                          />
                          <span className="text-sm text-gray-700">Confirmado</span>
                        </div>
                      )}

                      {req.config.itemType === 'number' && (
                        <Input
                          className="mt-2"
                          type="number"
                          value={typeof value === 'number' ? value : ''}
                          onChange={(e: ChangeEvent<HTMLInputElement>) =>
                            setChecklistValues((prev) => ({
                              ...prev,
                              [req.config.itemKey]: e.target.value ? Number(e.target.value) : null,
                            }))
                          }
                        />
                      )}

                      {(req.config.itemType === 'text' || req.config.itemType === 'textarea') && (
                        <Textarea
                          className="mt-2"
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            setChecklistValues((prev) => ({
                              ...prev,
                              [req.config.itemKey]: e.target.value,
                            }))
                          }
                          placeholder="Ingrese el valor"
                        />
                      )}

                      {req.config.itemType === 'select' && (
                        <Select
                          value={typeof value === 'string' ? value : ''}
                          onValueChange={(selected) =>
                            setChecklistValues((prev) => ({
                              ...prev,
                              [req.config.itemKey]: selected,
                            }))
                          }
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue placeholder="Selecciona una opción" />
                          </SelectTrigger>
                          <SelectContent>
                            {(req.config.options || []).map((option) => {
                              const optionValue = String(option);
                              return (
                                <SelectItem key={optionValue} value={optionValue}>
                                  {optionValue}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      )}

                      {isAttachment && (
                        <div className="mt-2 text-sm text-gray-600">
                          <p>
                            Archivos actuales: {String((req.value as { count?: number } | undefined)?.count ?? 0)}
                          </p>
                          {req.config.itemType === 'signature' ? (
                            <div className="space-y-2">
                              <p className="text-xs text-gray-500">
                                La firma se registra desde el enlace enviado al cliente.
                              </p>
                              <button
                                type="button"
                                className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs hover:bg-gray-50 disabled:opacity-60"
                                disabled={sendingSignatureKey === req.config.itemKey || !state?.bookingId}
                                onClick={async () => {
                                  if (!state?.bookingId) return;
                                  setLocalError(null);
                                  setSendingSignatureKey(req.config.itemKey);
                                  try {
                                    try {
                                      const jobData = await api.get<{ id: string }>(`/api/jobs/by-booking/${state.bookingId}`);
                                      const jobId = jobData?.id;
                                      if (!jobId) throw new Error('No se encontró trabajo vinculado para enviar firma');

                                      await api.post(`/api/jobs/${jobId}/send-signature-link`, {});
                                    } catch (err) {
                                      // If no job exists yet, fall back to booking-based endpoint.
                                      if (err instanceof Error && (err as { status?: number }).status === 404) {
                                        await api.post(`/api/bookings/${state.bookingId}/send-signature-link`, {});
                                      } else {
                                        throw err;
                                      }
                                    }
                                    setSignatureSentByKey((prev) => ({ ...prev, [req.config.itemKey]: true }));
                                  } catch (err) {
                                    setLocalError(err instanceof Error ? err.message : 'Error al enviar enlace de firma');
                                  } finally {
                                    setSendingSignatureKey(null);
                                  }
                                }}
                              >
                                {sendingSignatureKey === req.config.itemKey ? (
                                  <><Loader2 className="h-3.5 w-3.5 animate-spin" />Enviando…</>
                                ) : (
                                  <><Send className="h-3.5 w-3.5" />Enviar enlace de firma</>
                                )}
                              </button>
                              {signatureSentByKey[req.config.itemKey] && (
                                <p className="text-xs text-green-700">✓ Enlace de firma enviado</p>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-500">
                              Sube los archivos requeridos antes de continuar.
                            </p>
                          )}
                        </div>
                      )}

                      {req.config.itemType === 'photo' && source === 'booking_images' && (
                        <div className="mt-3 space-y-2">
                          <Label className="text-xs text-muted-foreground">Subir fotos</Label>
                          <label
                            className={cn(
                              'flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-4 text-sm transition-all duration-200',
                              uploadingKeys.has(req.config.itemKey)
                                ? 'border-blue-300 bg-blue-50 text-blue-700'
                                : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50/40'
                            )}
                          >
                            <UploadCloud className="h-4 w-4" />
                            <span>Arrastra o pulsa para seleccionar fotos</span>
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              capture="environment"
                              className="sr-only"
                              onChange={async (event) => {
                                const files = event.target.files ? Array.from(event.target.files) : [];
                                if (!files.length || !state) return;
                                const key = req.config.itemKey;
                                setUploadingKeys((prev) => new Set(prev).add(key));
                                setUploadingFileCounts((prev) => ({ ...prev, [key]: files.length }));
                                setUploadSuccessByKey((prev) => ({ ...prev, [key]: 0 }));
                                setUploadPreviewsByKey((prev) => {
                                  (prev[key] || []).forEach((url) => URL.revokeObjectURL(url));
                                  return { ...prev, [key]: files.map((file) => URL.createObjectURL(file)) };
                                });
                                try {
                                  const formData = new FormData();
                                  files.forEach((file) => formData.append('image', file));
                                  const response = await fetch(`${apiBase}/api/bookings/${state.bookingId}/images`, {
                                    method: 'POST',
                                    body: formData,
                                    credentials: 'include',
                                  });
                                  if (!response.ok) {
                                    const errorText = await response.text();
                                    let message = 'Error al subir imagen';
                                    try {
                                      const parsed = JSON.parse(errorText) as { error?: string };
                                      message = parsed.error || message;
                                    } catch {
                                      if (errorText) message = errorText;
                                    }
                                    throw new Error(message);
                                  }
                                  setRequirements((prev) =>
                                    prev.map((item) => {
                                      if (item.config.itemKey !== key) return item;
                                      const currentCount = typeof item.value === 'object' && item.value !== null && 'count' in item.value
                                        ? Number((item.value as { count?: number }).count ?? 0)
                                        : 0;
                                      const newCount = currentCount + files.length;
                                      const minCount = Number(item.config.validationRules?.minCount ?? 1);
                                      return {
                                        ...item,
                                        value: { count: newCount },
                                        isComplete: newCount >= minCount,
                                        errors: newCount >= minCount ? [] : item.errors,
                                      };
                                    })
                                  );
                                  setUploadSuccessByKey((prev) => ({ ...prev, [key]: files.length }));
                                  window.setTimeout(() => {
                                    setUploadSuccessByKey((prev) => ({ ...prev, [key]: 0 }));
                                  }, 2500);
                                  notifyUploadComplete();
                                } catch (err) {
                                  setLocalError(err instanceof Error ? err.message : 'Error al subir imagen');
                                } finally {
                                  event.target.value = '';
                                  setUploadingKeys((prev) => {
                                    const next = new Set(prev);
                                    next.delete(key);
                                    return next;
                                  });
                                  setUploadingFileCounts((prev) => ({ ...prev, [key]: 0 }));
                                }
                              }}
                              disabled={uploadingKeys.has(req.config.itemKey)}
                            />
                          </label>

                          {uploadingKeys.has(req.config.itemKey) && (
                            <p className="flex items-center gap-1.5 text-xs text-blue-700">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Subiendo {uploadingFileCounts[req.config.itemKey] ?? 0} archivos...
                            </p>
                          )}

                          {uploadSuccessByKey[req.config.itemKey] > 0 && !uploadingKeys.has(req.config.itemKey) && (
                            <p className="text-xs text-green-700">✓ {uploadSuccessByKey[req.config.itemKey]} fotos subidas</p>
                          )}

                          {uploadPreviewsByKey[req.config.itemKey]?.length ? (
                            <div className="rounded-lg border border-gray-200 p-2">
                              <div className="mb-2 flex items-center justify-between">
                                <span className="text-xs text-gray-500">Vista previa</span>
                                <span className="rounded-full bg-gray-100 px-2 py-0.5 text-[10px] font-medium text-gray-700">
                                  {uploadPreviewsByKey[req.config.itemKey].length} archivos
                                </span>
                              </div>
                              <div className="grid grid-cols-4 gap-2">
                                {uploadPreviewsByKey[req.config.itemKey].map((url) => (
                                  <div key={url} className="overflow-hidden rounded-md border border-gray-200 bg-gray-50">
                                    <img src={url} alt="Vista previa" className="h-16 w-full object-cover" />
                                  </div>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <p className="flex items-center gap-1 text-xs text-gray-500"><ImageIcon className="h-3.5 w-3.5" />Sin previsualizaciones todavía</p>
                          )}
                        </div>
                      )}

                      {isAttachment && req.config.itemType !== 'signature' && source !== 'booking_images' && (
                        <div className="mt-3">
                          <Label className="text-xs text-muted-foreground">Subir archivos</Label>
                          <label className={cn('flex cursor-pointer items-center justify-center gap-2 rounded-lg border border-dashed px-3 py-4 text-sm transition-all duration-200', uploadingKeys.has(req.config.itemKey) ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white hover:border-blue-300 hover:bg-blue-50/40')}><UploadCloud className="h-4 w-4" /><span>Seleccionar archivos</span><input
                            type="file"
                            accept="image/*"
                            multiple
                            capture="environment"
                            className="sr-only"
                            onChange={async (event) => {
                              const files = event.target.files ? Array.from(event.target.files) : [];
                              if (!files.length || !state) return;
                              const key = req.config.itemKey;
                              setUploadingKeys((prev) => new Set(prev).add(key));
                              try {
                                const formData = new FormData();
                                files.forEach((file) => formData.append('file', file));
                                const response = await fetch(
                                  `${apiBase}/api/bookings/${state.bookingId}/checklist/${req.config.id}/attachments`,
                                  {
                                    method: 'POST',
                                    body: formData,
                                    credentials: 'include',
                                  }
                                );
                                if (!response.ok) {
                                  const errorText = await response.text();
                                  let message = 'Error al subir archivo';
                                  try {
                                    const parsed = JSON.parse(errorText) as { error?: string };
                                    message = parsed.error || message;
                                  } catch {
                                    if (errorText) message = errorText;
                                  }
                                  throw new Error(message);
                                }
                                setRequirements((prev) =>
                                  prev.map((item) => {
                                    if (item.config.itemKey !== key) return item;
                                    const currentCount = typeof item.value === 'object' && item.value !== null && 'count' in item.value
                                      ? Number((item.value as { count?: number }).count ?? 0)
                                      : 0;
                                    const newCount = currentCount + files.length;
                                    const minCount = Number(item.config.validationRules?.minCount ?? 1);
                                    return {
                                      ...item,
                                      value: { count: newCount },
                                      isComplete: newCount >= minCount,
                                      errors: newCount >= minCount ? [] : item.errors,
                                    };
                                  })
                                );
                                notifyUploadComplete();
                              } catch (err) {
                                setLocalError(err instanceof Error ? err.message : 'Error al subir archivo');
                              } finally {
                                event.target.value = '';
                                setUploadingKeys((prev) => {
                                  const next = new Set(prev);
                                  next.delete(key);
                                  return next;
                                });
                              }
                            }}
                            disabled={uploadingKeys.has(req.config.itemKey)}
                          /></label>
                          {uploadingKeys.has(req.config.itemKey) && (
                            <p className="flex items-center gap-1.5 text-xs text-blue-700">
                              <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              Subiendo archivos...
                            </p>
                          )}
                        </div>
                      )}

                      {!isComplete && req.errors && req.errors.length > 0 && (
                        <div className="mt-2 text-xs text-red-600">
                          {req.errors.join(' · ')}
                        </div>
                      )}

                      {!isComplete && isRequired && (!req.errors || req.errors.length === 0) && (
                        <div className="mt-2 text-xs text-amber-700">
                          Pendiente: completa "{req.config.itemLabel}" para continuar.
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500">
                * Elementos obligatorios
              </p>
            </div>
          )}

          {customFieldDefs.length > 0 && (
            <div className="space-y-2">
              <Label className="flex items-center gap-1.5">
                <CheckSquare className="h-4 w-4" />
                Datos requeridos
              </Label>
              <div className="space-y-3">
                {customFieldDefs.map((field) => {
                  const value = customFields[field.name];
                  const isRequired = field.required;
                  const isFieldMissing = isRequired && (field.type === 'checkbox' ? value !== true : value === null || value === undefined || value === '');
                  const fieldWrapperClass = cn('space-y-1 rounded-lg p-2 transition-all duration-200', isFieldMissing ? 'bg-amber-50/40' : 'bg-transparent');
                  const label = (
                    <span className="text-sm font-medium text-gray-900">
                      {field.label}
                      {isRequired && <span className="text-red-500 ml-1">*</span>}
                    </span>
                  );

                  if (field.type === 'textarea') {
                    return (
                      <div key={field.name} className={fieldWrapperClass}>
                        {label}
                        <Textarea
                          value={typeof value === 'string' ? value : ''}
                          onChange={(e) =>
                            setCustomFields((prev) => ({ ...prev, [field.name]: e.target.value }))
                          }
                          placeholder={field.placeholder || ''}
                        />
                        {isFieldMissing && <p className="text-xs text-amber-700">Campo obligatorio pendiente</p>}
                      </div>
                    );
                  }

                  if (field.type === 'select') {
                    return (
                      <div key={field.name} className={fieldWrapperClass}>
                        {label}
                        <Select
                          value={typeof value === 'string' ? value : ''}
                          onValueChange={(selected) =>
                            setCustomFields((prev) => ({ ...prev, [field.name]: selected }))
                          }
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={field.placeholder || 'Selecciona una opcion'} />
                          </SelectTrigger>
                          <SelectContent>
                            {(field.options || []).map((option) => (
                              <SelectItem key={option.value} value={option.value}>
                                {option.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {isFieldMissing && <p className="text-xs text-amber-700">Campo obligatorio pendiente</p>}
                      </div>
                    );
                  }

                  if (field.type === 'checkbox') {
                    return (
                      <div key={field.name} className={cn('rounded-lg p-2 transition-all duration-200', isFieldMissing ? 'bg-amber-50/40' : 'bg-transparent')}>
                        <Checkbox
                          checked={value === true}
                          onCheckedChange={(checked: boolean) =>
                            setCustomFields((prev) => ({ ...prev, [field.name]: checked === true }))
                          }
                        />
                        {label}
                        {isFieldMissing && <p className="mt-1 text-xs text-amber-700">Campo obligatorio pendiente</p>}
                      </div>
                    );
                  }

                  if (field.type === 'file') {
                    return (
                      <div key={field.name} className={fieldWrapperClass}>
                        {label}
                        <Input type="file" disabled />
                        <p className="text-xs text-muted-foreground">
                          Adjunta este archivo desde la seccion de documentos.
                        </p>
                        {isFieldMissing && <p className="text-xs text-amber-700">Campo obligatorio pendiente</p>}
                      </div>
                    );
                  }

                  const inputType = field.type === 'number'
                    ? 'number'
                    : field.type === 'email'
                      ? 'email'
                      : field.type === 'phone'
                        ? 'tel'
                        : field.type === 'date'
                          ? 'date'
                          : 'text';

                  return (
                    <div key={field.name} className="space-y-1">
                      {label}
                      <Input
                        type={inputType}
                        value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''}
                        onChange={(e) =>
                          setCustomFields((prev) => ({
                            ...prev,
                            [field.name]:
                              field.type === 'number'
                                ? (e.target.value ? Number(e.target.value) : null)
                                : e.target.value,
                          }))
                        }
                        placeholder={field.placeholder || ''}
                        min={field.min}
                        max={field.max}
                      />
                      {isFieldMissing && <p className="text-xs text-amber-700">Campo obligatorio pendiente</p>}
                    </div>
                  );
                })}
              </div>
              <p className="text-xs text-gray-500">
                * Campos obligatorios
              </p>
            </div>
          )}

          {/* Bay selector for IN_PROGRESS transition */}
          {isBaySelect && (
            <div className="space-y-2">
              <Label htmlFor="bay-select">Puesto de trabajo *</Label>
              {isLoadingBays ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Cargando puestos disponibles...
                </div>
              ) : availableBays && availableBays.length > 0 ? (
                <Select value={selectedBayId} onValueChange={setSelectedBayId}>
                  <SelectTrigger id="bay-select">
                    <SelectValue placeholder="Selecciona un puesto" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableBays.map((bay) => (
                      <SelectItem key={bay.id} value={bay.id}>
                        <span className="flex items-center gap-2">
                          <span>{bay.name}</span>
                          <span className="text-xs text-gray-400">
                            ({bay.bayType === 'bodywork' ? 'Chapa' : bay.bayType === 'mechanical' ? 'Mecánica' : 'Diagnóstico'})
                          </span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="rounded-md bg-yellow-50 p-3 text-sm text-yellow-700">
                  No hay puestos disponibles del tipo requerido ({serviceBayType ? BAY_TYPE_LABELS[serviceBayType] : 'cualquiera'}).
                  Por favor, contacta con administración.
                </div>
              )}
              {serviceBayType && (
                <p className="text-xs text-gray-500">
                  Tipo de trabajo: {BAY_TYPE_LABELS[serviceBayType] || serviceBayType}
                </p>
              )}
            </div>
          )}

          {/* Quote line items form */}
          {isQuote && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>Conceptos del presupuesto *</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLineItem}
                  className="h-7 text-xs"
                >
                  <Plus className="h-3 w-3 mr-1" />
                  Añadir
                </Button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {lineItems.map((item) => (
                  <div key={item.id} className="flex gap-2 p-2 bg-gray-50 rounded-lg">
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={item.description}
                        onChange={(e) => updateLineItem(item.id, 'description', e.target.value)}
                        placeholder="Descripción del concepto..."
                        className="w-full px-2 py-1.5 text-sm border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                      <div className="flex gap-2">
                        <Select
                          value={item.category}
                          onValueChange={(value) => updateLineItem(item.id, 'category', value)}
                        >
                          <SelectTrigger className="h-8 text-xs w-28">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ESTIMATE_LINE_ITEM_CATEGORIES.map((cat) => (
                              <SelectItem key={cat.value} value={cat.value}>
                                {cat.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="1"
                            value={item.quantity}
                            onChange={(e) => updateLineItem(item.id, 'quantity', parseInt(e.target.value) || 1)}
                            className="w-14 px-2 py-1 text-sm text-center border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-500">×</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={(item.unitPrice / 100).toFixed(2)}
                            onChange={(e) => updateLineItem(item.id, 'unitPrice', Math.round(parseFloat(e.target.value || '0') * 100))}
                            placeholder="0.00"
                            className="w-20 px-2 py-1 text-sm text-right border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <span className="text-xs text-gray-500">€</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-col justify-between items-end">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeLineItem(item.id)}
                        disabled={lineItems.length === 1}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                      <span className="text-xs font-medium text-gray-600">
                        {((item.quantity * item.unitPrice) / 100).toFixed(2)} €
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Total */}
              <div className="flex justify-between items-center pt-2 border-t">
                <span className="text-sm font-medium text-gray-600">Total presupuesto:</span>
                <span className="text-lg font-bold text-green-600">
                  {(totalEstimate / 100).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                </span>
              </div>
            </div>
          )}

          {/* Cancellation reason selector */}
          {isCancellation && (
            <div className="space-y-2">
              <Label htmlFor="cancel-reason">Motivo de cancelación *</Label>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger id="cancel-reason">
                  <SelectValue placeholder="Selecciona un motivo" />
                </SelectTrigger>
                <SelectContent>
                  {CANCELLATION_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Notes input */}
          {((state.config.dialogType === 'notes' || isCancellation || dialogConfig.showNotes) && !isQuote) && (
            <div className="space-y-2">
              <Label htmlFor="transition-notes">
                Notas internas {dialogConfig.notesRequired && !isCancellation ? '*' : '(opcional)'}
              </Label>
              <textarea
                id="transition-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Notas internas sobre este cambio..."
                className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                maxLength={500}
              />
            </div>
          )}

          {/* Public note input */}
          {dialogConfig.showPublicNote && (
            <div className="space-y-2">
              <Label htmlFor="public-note">
                Nota para cliente (opcional)
              </Label>
              <textarea
                id="public-note"
                value={publicNote}
                onChange={(e) => setPublicNote(e.target.value)}
                placeholder="Esta nota será visible para el cliente..."
                className="w-full px-3 py-2 border rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500"
                rows={2}
                maxLength={500}
              />
              <p className="text-xs text-gray-500">
                El cliente podrá ver esta nota en el seguimiento de su cita.
              </p>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {error}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={onCancel}
            disabled={isTransitioning}
          >
            Cancelar
          </Button>
          <Button
            variant={isCancellation ? 'destructive' : 'default'}
            onClick={handleExecute}
            disabled={
              isTransitioning ||
              hasBlockingErrors ||
              (isCancellation && !reason) ||
              (isBaySelect && !selectedBayId) ||
              (isBaySelect && isLoadingBays) ||
              (isQuote && lineItems.filter(i => i.description.trim() && i.unitPrice > 0).length === 0) ||
              hasIncompleteRequirements ||
              hasIncompleteCustomFields ||
              (dialogConfig.notesRequired && !isCancellation && !notes.trim())
            }
          >
            {isTransitioning ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Procesando...
              </>
            ) : isCancellation ? (
              'Confirmar Cancelación'
            ) : isCompletion ? (
              'Completar'
            ) : isBaySelect ? (
              'Iniciar Trabajo'
            ) : isQuote ? (
              'Crear Presupuesto'
            ) : (
              'Confirmar'
            )}
          </Button>
          {(missingChecks.length > 0 || hasBlockingErrors) && (
            <p className="w-full text-right text-xs text-amber-700 sm:pr-2">
              {hasBlockingErrors
                ? 'Existen bloqueos de transición que debes resolver.'
                : `Falta: ${missingChecks.slice(0, 4).join(', ')}${missingChecks.length > 4 ? ` y ${missingChecks.length - 4} más` : ''}`}
            </p>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
